import React from 'react';
import Tabs from 'react-bootstrap/Tabs';
import { Tab } from 'react-bootstrap';
import _ from 'lodash';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { RequestTabState, title } from '../RequestTab/RequestTab';
import i18n from 'i18n';
import { DelayType, ResponseDelay } from 'types/rest/mockServiceTypes';
import ThinkTimeRadioGroup from 'views/MockServices/Tabs/MockServices/Params/ThinkTimeRadioGroup';
import { MessagingRequestTabState } from '../RequestTab/MessagingRequestTab';
import ResponseBody from '../../../../../components/transactionEditor/ResponseBody';
import { ContentType, messageTypes } from '../../../../../types/rest/dslTypes';
import BzDropdownButton, { DropdownButtonItem } from '../../../../../components/buttons/BzDropdownButton';
import ResponseKeyValueList, {
  ResponseKeyValueItem,
} from '../../../../../components/transactionEditor/ResponseKeyValueList';

interface MessagingResponseTabProps {
  state: MessagingResponseTabState;
  msgRequestTabState: MessagingRequestTabState;
  updateState: (state: MessagingResponseTabState) => void;
  isReadOnly?: boolean;
  isResponseBodyMagicStringInvalid?: boolean;
  isResponseHeaderMagicStringInvalid?: boolean;
  resBodyInvalidMagicStrings?: string;
  resHeaderInvalidMagicStrings?: string;
  activeResponseTab: string;
  activeResponseTabChanged: (activeTab: string) => void;
  serviceId?: number;
}

export const messageTypeOptions: DropdownButtonItem[] = [];
Object.keys(messageTypes).forEach(function(key) {
  messageTypeOptions.push({
    key: key,
    title: messageTypes[key],
  });
});
export const defaultMessageType: DropdownButtonItem = messageTypeOptions.find(
  dOp => 'TEXT_MESSAGE' === dOp.key,
) as DropdownButtonItem;

export interface MessagingResponseTabState {
  messageType: string;
  content: string;
  charset: string;
  headers: ResponseKeyValueItem[];
  properties: ResponseKeyValueItem[];
  responseDelay?: ResponseDelay;
  failoverEnabled?: boolean;
  isValid: boolean;
  binary: boolean;
  dirty: boolean;
  contentType: ContentType;
  [key: string]: string | number | boolean | null | ResponseDelay | ResponseKeyValueItem[];
}

const MessagingResponseTab = (props: MessagingResponseTabProps) => {
  const operatorToSelect: DropdownButtonItem =
    props.state.messageType !== undefined
      ? {
          key: props.state.messageType,
          title: messageTypes[props.state.messageType],
        }
      : defaultMessageType;

  const updateState = (field: string, value: any) => {
    const updatedState = _.cloneDeep(props.state);
    _.set(updatedState, field, value);
    updatedState.dirty = true;
    props.updateState(updatedState);
  };

  const updateResponseDelay = (value: ResponseDelay) => {
    const updatedState = _.cloneDeep(props.state);
    _.set(updatedState, 'responseDelay', value);
    _.set(updatedState, 'isValid', true);
    updatedState.dirty = true;
    props.updateState(updatedState);
  };

  const isThinkTimeSet = (): boolean => {
    return !_.isNull(props.state.responseDelay) && props.state.responseDelay?.type !== DelayType.NONE;
  };

  const requestTabState: RequestTabState = {
    ...props.msgRequestTabState,
    headers: [],
    queryParams: [],
    cookies: [],
    credentials: null,
    method: '',
    host: '',
    path: '',
    url: null,
  };

  return (
    <div className="bg-light p-4">
      <Row>
        <Col md={3}>
          <div id={'messageTypeId'} className="mb-4 mt-1">
            <label className={'font-size-lg font-weight-semi-bold'} htmlFor={`qa-toggle-messageTypeId`}>
              {i18n.t('transactions:editor.messageType')}
            </label>
            <BzDropdownButton
              disabled={props.isReadOnly}
              id={'messageTypeId'}
              dropdownItems={messageTypeOptions}
              selectedItem={operatorToSelect}
              onSelectedItem={(item: DropdownButtonItem) => updateState('messageType', item.key)}
              variant="secondary"
              maxHeight={'300px'}
            />
          </div>
        </Col>
        <Col></Col>
      </Row>

      <Tabs
        id="qa-tabs-response"
        activeKey={props.activeResponseTab}
        onSelect={(key: string) => props.activeResponseTabChanged(key)}
      >
        <Tab title={i18n.t('transactions:editor.bodyTitle')} eventKey="body">
          <ResponseBody
            isReadOnly={props.isReadOnly}
            onChange={updateState}
            binary={props.state.binary}
            content={props.state.content}
            isResponseBodyMagicStringInvalid={props.isResponseBodyMagicStringInvalid}
            resBodyInvalidMagicStrings={props.resBodyInvalidMagicStrings}
            requestTabState={requestTabState}
            serviceId={props.serviceId}
          />
        </Tab>
        <Tab
          title={title(i18n.t('transactions:editor.responseHeaders'), props.state.headers.length)}
          eventKey="response-headers"
        >
          <ResponseKeyValueList
            isReadOnly={props.isReadOnly}
            id="response-header-list"
            responseItemList={props.state.headers}
            emptyListMessage={i18n.t('transactions:editor.noResponseHeaders')}
            onListChange={list => updateState('headers', list)}
            resHeaderInvalidMagicStrings={props.resHeaderInvalidMagicStrings}
            isResponseHeaderMagicStringInvalid={props.isResponseHeaderMagicStringInvalid}
          />
        </Tab>
        <Tab
          title={title(i18n.t('transactions:editor.responseProperties'), props.state.properties.length)}
          eventKey="response-properties"
        >
          <ResponseKeyValueList
            isReadOnly={props.isReadOnly}
            id="response-properties-list"
            responseItemList={props.state.properties}
            emptyListMessage={i18n.t('transactions:editor.noResponseProperties')}
            onListChange={list => updateState('properties', list)}
            isMessageProperty={true}
          />
        </Tab>
        <Tab
          title={title(
            i18n.t('common:thinkTime.title'),
            isThinkTimeSet()
              ? `${i18n.t('transactions:editor.defined')}`
              : `${i18n.t('transactions:editor.notDefined')}`,
          )}
          eventKey="response-thinkTime"
        >
          <Col md={3}>
            <ThinkTimeRadioGroup
              responseDelay={props.state.responseDelay}
              updateResponseDelay={updateResponseDelay}
              updateState={updateState}
              noDelayText={i18n.t('common:thinkTime.txNoDelay')}
            />
          </Col>
        </Tab>
      </Tabs>
    </div>
  );
};

export default MessagingResponseTab;
